"use client"

import { Transition, TransitionChild } from "@headlessui/react"
import { useEffect, useRef, useState } from "react"
import { Button } from "@ui/button"
import { Input } from "@ui/input"
import { BasicChatMessage, ChatMessage } from "../../../assistants/[id]/components/ChatMessage"
import { MessageAuthor } from "@graphql/assistants/graphql"
import { clsx } from "clsx"
import { FeedbackForm } from "./FeedbackForm"
import { NoAnswer } from "./NoAnswer"
import mixpanel from "mixpanel-browser"
import { v4 as uuidv4 } from "uuid"
import { SourcesFooter } from "./SourcesFooter"

type State = {
    outcome: "not_started" | "answer" | "error" | "no_answer"
    answer?: string
    sources?: string[]
}

function generateCorrelationId() {
    return uuidv4()
}

export function AppV2() {
    const [question, setQuestion] = useState("")
    const [state, setState] = useState<State>({ outcome: "not_started" })
    const [visibleAnswer, setVisibleAnswer] = useState("")
    const [typing, setTyping] = useState(false)
    const [working, setWorking] = useState(false)
    const [showFeedback, setShowFeedback] = useState(false)
    const [showEscalate, setShowEscalate] = useState(false)
    const [showError, setShowError] = useState(false)
    const [correlationId, setCorrelationId] = useState(generateCorrelationId())
    const inputRef = useRef<HTMLInputElement>(null)

    useEffect(() => {
        if (state.outcome === "answer" && state.answer) {
            setTyping(true)
            const interval = setInterval(() => {
                setVisibleAnswer((prev) => {
                    if (prev.length < state.answer!.length) {
                        return state.answer!.slice(0, prev.length + 1)
                    }
                    setTyping(false)
                    clearInterval(interval)
                    return state.answer!
                })
            }, 10)
            return () => clearInterval(interval)
        } else if (state.outcome === "no_answer") {
            setShowEscalate(true)
        } else if (state.outcome === "error") {
            setShowError(true)
        }
    }, [state.answer, state.outcome])

    useEffect(() => {
        if (typing === false && state.outcome === "answer" && state.answer === visibleAnswer) {
            setShowFeedback(true)
        }
    }, [typing, state.answer, state.outcome, visibleAnswer])

    const submit = (formData: FormData) => {
        setWorking(true)
        setQuestion(formData.get("question") as string)
        mixpanel.track("Virtual Ray Question Asked", { correlationId })
        fetch("/labs/virtual-ray/messages", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({ question: formData.get("question"), correlationId }),
        })
            .then((response) => response.json() as Promise<State>)
            .then((data) => {
                mixpanel.track("Virtual Ray Question Answered", { correlationId, outcome: data.outcome })
                setState(data)
            })
            .catch(() => {
                mixpanel.track("Virtual Ray Question Error", { correlationId })
                setState({ outcome: "error" })
            })
            .finally(() => {
                setWorking(false)
            })
    }

    const reset = () => {
        setQuestion("")
        setVisibleAnswer("")
        setState({ outcome: "not_started" })
        setShowFeedback(false)
        setShowEscalate(false)
        setShowError(false)
        setCorrelationId(generateCorrelationId())
        inputRef.current?.focus()
    }

    return (
        <>
            <Transition
                as="div"
                show={state.outcome === "not_started" && !working}
                leave="transition-opacity duration-100"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
            >
                <TransitionChild>
                    <form
                        action={submit}
                        className="data-[closed]:height-0 flex w-full flex-col transition-[height] lg:mx-auto lg:w-[36rem] lg:flex-row lg:space-x-2"
                    >
                        <Input name="question" ref={inputRef} required className="mb-2 lg:mb-0" />
                        <Button type="submit" color="primary" className="mx-auto w-1/2 lg:mx-0 lg:w-auto">
                            Submit
                        </Button>
                    </form>
                </TransitionChild>
            </Transition>
            {/* {(state.outcome !== "not_started" || working) && ( */}
            <Transition show={state.outcome !== "not_started" || working} appear={true}>
                <div
                    className={clsx([
                        "transition-width h-[60vh] w-screen transition-[height] data-[enter]:duration-1000 lg:w-[56rem]",
                        "data-[closed]:size-0",
                        "data-[leave]:duration-0",
                    ])}
                >
                    <TransitionChild>
                        <div
                            className={clsx([
                                "flex flex-col-reverse space-y-2 overflow-y-auto p-4 ease-in",
                                "transition-opacity",
                                "data-[enter]:delay-1000 data-[enter]:duration-500",
                                "data-[leave]:delay-0 data-[leave]:duration-0",
                                "data-[closed]:opacity-0",
                            ])}
                        >
                            <Transition as="div" show={showEscalate} appear={true}>
                                <BasicChatMessage alignment="left">
                                    <NoAnswer question={question} previousMessages={[]} correlationId={correlationId} />
                                </BasicChatMessage>
                                <div className="flex justify-center">
                                    <Button onClick={reset}>Reset</Button>
                                </div>
                            </Transition>
                            <Transition as="div" show={showError} appear={true}></Transition>
                            <Transition as="div" show={showFeedback} appear={true}>
                                <FeedbackForm correlationId={correlationId} />
                                <div className="flex justify-center">
                                    <Button onClick={reset}>Reset</Button>
                                </div>
                            </Transition>
                            {!showError && !showEscalate && (
                                <>
                                    {visibleAnswer.length === 0 ? (
                                        <div className={`my-2 flex justify-start`}>
                                            <div
                                                className={`h-24 w-[70%] animate-pulse rounded-lg bg-gray-300 p-3`}
                                            ></div>
                                        </div>
                                    ) : (
                                        <div>
                                            {typing ? (
                                                <ChatMessage
                                                    message={{ author: MessageAuthor.Ai, content: visibleAnswer }}
                                                />
                                            ) : (
                                                <ChatMessage
                                                    message={{ author: MessageAuthor.Ai, content: visibleAnswer }}
                                                    footer={<SourcesFooter sources={state.sources} />}
                                                />
                                            )}
                                        </div>
                                    )}
                                </>
                            )}
                            <BasicChatMessage alignment="right">
                                <span className="px-4">{question}</span>
                            </BasicChatMessage>
                            {/* <ChatMessage message={{ author: MessageAuthor.Human, content: question }} /> */}
                        </div>
                    </TransitionChild>
                </div>
            </Transition>
            {/* )} */}
        </>
    )
}
